/**
 * ExpandableRichText
 *
 * Generic expandable text with hashtag, mention, and URL support.
 * Used for video descriptions and user bios.
 * Matches Flutter's AppExpandableText.
 *
 * Features:
 * - Expandable text with "Show more"/"Show less" via clamping
 * - Hashtag highlighting and click handling (#hashtag)
 * - Mention highlighting and click handling (@username)
 * - URL detection and click handling
 * - Configurable max lines before expansion
 */

import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { appLogger } from '@/domain/services/analytics/appLogger';
import { qaAttr } from '@/presentation/utils/qaAttr';

export interface ExpandableRichTextProps {
  description?: string | null;
  maxLines?: number;
  toggleExpand?: boolean;
  style?: React.CSSProperties;
  onHashtagTap?: (hashtag: string) => Promise<void> | void;
  onMentionTap?: (mention: string) => Promise<void> | void;
  onUrlTap?: (url: string) => Promise<void> | void;
  className?: string;
}

const parseDescription = (text: string) => {
  const parts: Array<{ type: 'text' | 'hashtag' | 'mention' | 'url'; content: string }> = [];

  const hashtagPattern = /#[\w]+/g;
  const mentionPattern = /@[\w]+/g;
  const urlPattern = /(https?:\/\/[^\s]+)|(www\.[^\s]+)|([a-zA-Z0-9][a-zA-Z0-9-]+\.[a-zA-Z]{2,}[^\s]*)/g;

  let lastIndex = 0;
  const matches: Array<{ type: 'hashtag' | 'mention' | 'url'; index: number; content: string }> = [];

  let match;
  while ((match = hashtagPattern.exec(text)) !== null) {
    matches.push({ type: 'hashtag', index: match.index, content: match[0] });
  }

  while ((match = mentionPattern.exec(text)) !== null) {
    matches.push({ type: 'mention', index: match.index, content: match[0] });
  }

  while ((match = urlPattern.exec(text)) !== null) {
    matches.push({ type: 'url', index: match.index, content: match[0] });
  }

  matches.sort((a, b) => a.index - b.index);

  matches.forEach((m) => {
    if (m.index > lastIndex) {
      parts.push({ type: 'text', content: text.substring(lastIndex, m.index) });
    }
    parts.push({ type: m.type, content: m.content });
    lastIndex = m.index + m.content.length;
  });

  if (lastIndex < text.length) {
    parts.push({ type: 'text', content: text.substring(lastIndex) });
  }

  return parts;
};

// Block insecure http:// URLs and prepend https:// when missing.
const sanitizeUrl = (url: string): string | null => {
  if (url.startsWith('http://')) {
    return null;
  }

  if (!url.startsWith('https://')) {
    if (url.startsWith('www.')) {
      return `https://${url}`;
    } else {
      return `https://www.${url}`;
    }
  }

  return url;
};

export const ExpandableRichText: React.FC<ExpandableRichTextProps> = ({
  description,
  maxLines = 3,
  toggleExpand = true,
  style,
  onHashtagTap,
  onMentionTap,
  onUrlTap,
  className = ''
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const [isExpanded, setIsExpanded] = useState(false);
  const [shouldShowToggle, setShouldShowToggle] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        const lineHeight = parseInt(window.getComputedStyle(textRef.current).lineHeight);
        const maxHeight = lineHeight * maxLines;
        setShouldShowToggle(textRef.current.scrollHeight > maxHeight);
      }
    };

    checkTruncation();
    window.addEventListener('resize', checkTruncation);
    return () => {
      window.removeEventListener('resize', checkTruncation);
    };
  }, [description, maxLines]);

  if (!description) {
    return null;
  }

  const cleanDescription = description.startsWith(' ') ? description.substring(1) : description;
  const parts = parseDescription(cleanDescription);

  const handleHashtagClick = (hashtag: string) => {
    const cleanHashtag = hashtag.substring(1);
    onHashtagTap?.(cleanHashtag);
  };

  const handleMentionClick = (mention: string) => {
    const cleanMention = mention.substring(1);
    onMentionTap?.(cleanMention);
  };

  const handleUrlClick = (url: string) => {
    const sanitized = sanitizeUrl(url);
    if (!sanitized) {
      appLogger.warning(`Unsafe URL blocked: ${url}`, 'ExpandableRichText.handleUrlClick');
      return;
    }

    if (onUrlTap) {
      onUrlTap(sanitized);
    } else {
      window.open(sanitized, '_blank', 'noopener,noreferrer');
    }
  };

  const handleTextClick = (e: React.MouseEvent) => {
    if (toggleExpand && shouldShowToggle) {
      const target = e.target as HTMLElement;
      if (target.tagName !== 'BUTTON') {
        setIsExpanded(!isExpanded);
      }
    }
  };

  return (
    <div className={className}>
      <div
        ref={textRef}
        {...(toggleExpand && shouldShowToggle
          ? qaAttr({ id: 'expandable-text-toggle', status: isExpanded ? 'expanded' : 'collapsed' })
          : {})}
        onClick={handleTextClick}
        style={{
          color: theme.text.secondary,
          fontSize: responsive.font.base,
          fontWeight: responsive.fontWeight.regular,
          lineHeight: '1.7',
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitLineClamp: isExpanded ? 'unset' : maxLines,
          WebkitBoxOrient: 'vertical',
          cursor: toggleExpand && shouldShowToggle ? 'pointer' : 'default',
          ...style
        }}
      >
        {parts.map((part, index) => {
          if (part.type === 'hashtag') {
            return (
              <button
                key={index}
                onClick={() => handleHashtagClick(part.content)}
                className="hover:opacity-80 transition-opacity focus:outline-none"
                style={{
                  color: theme.blue,
                  fontWeight: responsive.fontWeight.medium,
                  cursor: 'pointer',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  font: 'inherit'
                }}
              >
                {part.content}
              </button>
            );
          }

          if (part.type === 'mention') {
            return (
              <button
                key={index}
                onClick={() => handleMentionClick(part.content)}
                className="hover:opacity-80 transition-opacity focus:outline-none"
                style={{
                  color: theme.blue,
                  fontWeight: responsive.fontWeight.medium,
                  cursor: 'pointer',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  font: 'inherit'
                }}
              >
                {part.content}
              </button>
            );
          }

          if (part.type === 'url') {
            return (
              <button
                key={index}
                onClick={() => handleUrlClick(part.content)}
                className="hover:opacity-80 transition-opacity focus:outline-none"
                style={{
                  color: theme.white,
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  font: 'inherit'
                }}
              >
                {part.content}
              </button>
            );
          }

          return <span key={index}>{part.content}</span>;
        })}
      </div>
    </div>
  );
};
